import { watch, onMounted, type Ref } from 'vue';

export type FormFieldType = 'input' | 'textarea' | 'select' | 'autocomplete' | 'combobox';

/**
 * Composable for form field event handling and focus management
 * Provides reusable logic for keyboard events and field focusing
 *
 * @param fieldRef - Reference to the field component
 * @param formFieldRef - Reference to the form field wrapper (optional)
 * @param attrs - Component attributes
 * @param emit - Emit function for events
 * @param fieldType - Type of field for specific element selection (default: 'input')
 */
export function useHFormField(
  fieldRef: Ref<any>,
  formFieldRef: Ref<any> | null,
  attrs: Record<string, any>,
  emit: (event: string, ...args: any[]) => void,
  fieldType: FormFieldType = 'input',
) {
  /**
   * Handles keyboard events for form fields
   * Supports Enter and Escape keys with conditional event propagation
   */
  const handleKeydown = (event: KeyboardEvent) => {
    if (event.key === 'Enter') {
      const hasListener = 'onKeydown:enter' in attrs;
      if (hasListener) {
        emit('keydown:enter', event);
      } else {
        // For textarea, allow default Enter behavior (newline) unless parent explicitly handles it
        if (fieldType === 'textarea') {
          event.stopPropagation();
        } else {
          event.preventDefault();
          event.stopPropagation();
        }
      }
    } else if (event.key === 'Escape') {
      const hasListener = 'onKeydown:esc' in attrs;
      if (hasListener) {
        emit('keydown:esc', event);
      } else {
        event.preventDefault();
        event.stopPropagation();
      }
    }
  };

  /**
   * Gets the appropriate element selector based on field type
   */
  const getElementSelector = (): string => {
    switch (fieldType) {
      case 'textarea':
        return 'textarea';
      case 'select':
      case 'autocomplete':
      case 'combobox':
      case 'input':
      default:
        return 'input';
    }
  };

  /**
   * Sets up focus functionality for the field
   * Tries multiple methods to focus the input/textarea element
   */
  const setupFieldFocus = (field: any) => {
    if (!formFieldRef || !formFieldRef.value || !field) return;

    const selector = getElementSelector();

    formFieldRef.value.fieldRef = {
      focus: () => {
        // Try multiple ways to focus the element
        if (field?.focus) {
          field.focus();
        } else if (field?.$el) {
          const element = field.$el.querySelector(selector);
          if (element) element.focus();
        }
      },
    };
  };

  /**
   * Focus the field programmatically
   */
  const focus = () => {
    if (fieldRef.value?.focus) {
      fieldRef.value.focus();
    } else if (fieldRef.value?.$el) {
      const selector = getElementSelector();
      const element = fieldRef.value.$el.querySelector(selector);
      if (element) element.focus();
    }
  };

  // Set up focus on mount
  onMounted(() => {
    setupFieldFocus(fieldRef.value);
  });

  // Watch for field ref changes and update focus setup
  watch(fieldRef, (newVal) => {
    setupFieldFocus(newVal);
  });

  return {
    handleKeydown,
    focus,
  };
}
